import {
  SDKClientEvents,
  MESSAGE_START_FLOW_WITH_TOKEN,
  StorageKeys,
  WidgetZIndex,
} from '@usertour/constants';
import { AssetAttributes } from '@usertour/frame';
import {
  isConditionsActived,
  isEmptyString,
  isNullish,
  normalizeLegacyAttributeWrites,
  storage,
  uuidV4,
} from '@usertour/helpers';
import {
  contentStartReason,
  SDKSettingsMode,
  UserTourTypes,
  CustomContentSession,
  TrackCondition,
  ConditionWaitTimer,
  ClientCondition,
  WebSocketEvents,
} from '@usertour/types';
import { Evented } from '@/utils/evented';
import { ExternalStore } from '@/utils/store';
import { UsertourTour } from '@/core/usertour-tour';
import { UsertourSession } from '@/core/usertour-session';
import { UsertourSocket } from '@/core/usertour-socket';
import { UsertourAttributeManager } from '@/core/usertour-attribute-manager';
import {
  ConditionStateChangeEvent,
  UsertourConditionsMonitor,
} from '@/core/usertour-conditions-monitor';
import {
  WaitTimerStateChangeEvent,
  ConditionWaitTimersMonitor,
} from '@/core/usertour-wait-timer-monitor';
import { UsertourURLMonitor } from '@/core/usertour-url-monitor';
import { UsertourUIManager } from '@/core/usertour-ui-manager';
import {
  autoBind,
  document,
  window,
  on,
  logger,
  getValidMessage,
  sendPreviewSuccessMessage,
  timerManager,
} from '@/utils';
import { getClientContext, setUrlFilterFn } from '@/core/usertour-helper';
import { rulesEvaluatorManager } from '@/core/usertour-rules-evaluator';
import { ErrorMessages } from '@/types';
import { formatErrorMessage } from '@/types/error-messages';
import { UsertourChecklist } from './usertour-checklist';
import { UsertourLauncher } from './usertour-launcher';
import { UsertourBanner } from './usertour-banner';
import { UsertourResourceCenter } from './usertour-resource-center';
import { customInputRegistry } from './registries/custom-input-registry';
import {
  ServerMessageHandlerManager,
  ServerMessageHandlerContext,
} from './server-message-handlers';

const log = logger.scope('core');

/**
 * The keys the server refused. They leave the local cache (ADR 0020 §6):
 * what the server holds for them is unknown here, and a stale literal would
 * make the next call carrying the key look unchanged and go unsent. A company
 * acknowledgement names refused keys without their scope, so a refused key
 * leaves the company and the membership cache alike; the same-named key the
 * other scope accepted is then sent once more, which costs one upsert.
 */
const refusedCodeNames = (rejected: UserTourTypes.AttributesWriteResult['rejected']): string[] => {
  return rejected.map((entry) => entry.codeName);
};
interface AppStartOptions {
  environmentId?: string;
  mode: SDKSettingsMode;
  contentId?: string;
  versionId?: string;
  token: string;
}

/**
 * Signature-blind peek at a JWT's payload claims (base64url decode only).
 * Used solely for developer warnings — the server stays the source of truth
 * for verification.
 */
const decodeTokenClaims = (token: string): Record<string, unknown> | null => {
  try {
    const segments = token.split('.');
    if (segments.length !== 3) {
      return null;
    }
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const parsed = JSON.parse(atob(padded));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

export class UsertourCore extends Evented {
  // === Public Properties ===
  socketService: UsertourSocket;
  startOptions: AppStartOptions = {
    environmentId: '',
    token: '',
    mode: SDKSettingsMode.NORMAL,
  };
  toursStore = new ExternalStore<UsertourTour[]>([]);
  checklistsStore = new ExternalStore<UsertourChecklist[]>([]);
  launchersStore = new ExternalStore<UsertourLauncher[]>([]);
  bannersStore = new ExternalStore<UsertourBanner[]>([]);
  resourceCentersStore = new ExternalStore<UsertourResourceCenter[]>([]);
  activatedTour: UsertourTour | null = null;
  activatedChecklist: UsertourChecklist | null = null;
  activatedBanner: UsertourBanner | null = null;
  activatedResourceCenter: UsertourResourceCenter | null = null;
  launchers: UsertourLauncher[] = [];
  // Active trackers keyed by contentId
  private activeTrackers = new Map<
    string,
    {
      session: CustomContentSession;
      isActive: boolean;
    }
  >();
  assets: AssetAttributes[] = [];
  externalUserId: string | undefined;
  externalCompanyId: string | undefined;
  // Identity token (customer-backend-signed JWT, ADR 0009) supplied by the
  // host app. Kept alongside the ids so reconnect handshakes re-present it.
  private identityToken: string | undefined;

  // === Private Properties ===
  private baseZIndex = WidgetZIndex.BASE;
  private targetMissingSeconds: number | undefined = undefined;
  private customNavigate: ((url: string) => void) | null = null;
  private customScrollIntoView: ((el: Element) => void) | null = null;
  private urlFilter: ((url: string) => string) | null = null;
  private linkUrlDecorator: ((url: string) => string) | null = null;
  private evalJsDisabled = false;
  private resourceCenterLauncherHidden = false;
  private readonly id: string;
  private attributeManager: UsertourAttributeManager;
  private uiManager: UsertourUIManager;
  private conditionsMonitor: UsertourConditionsMonitor | null = null;
  private waitTimerMonitor: ConditionWaitTimersMonitor | null = null;
  private urlMonitor: UsertourURLMonitor | null = null;
  private trackerMonitorIntervalId: string | null = null;
  private isTrackerMonitorRunning = false;
  private serverMessageHandlerManager: ServerMessageHandlerManager;
  // Map of sessionId to Set of unacked task IDs
  private taskIsUnacked = new Map<string, Set<string>>();
  // Set of session IDs that have been dismissed by the user
  // Prevents re-displaying sessions that the user has explicitly closed
  private dismissedSessionIds = new Set<string>();
  // Server message handler context (initialized in initializeServerMessageHandlerContext)
  private serverMessageHandlerContext!: ServerMessageHandlerContext;

  // === Constructor ===
  constructor() {
    super();
    autoBind(this);
    this.socketService = new UsertourSocket();
    this.attributeManager = new UsertourAttributeManager();
    this.serverMessageHandlerManager = new ServerMessageHandlerManager();
    this.uiManager = new UsertourUIManager();
    this.id = uuidV4();
    this.initializeServerMessageHandlerContext();
    this.initializeEventListeners();
    this.initializeSocketEventListeners();
    this.initializeConditionsMonitor();
    this.initializeWaitTimerMonitor();
    this.initializeURLMonitor();
  }

  // === Public API: Initialization ===
  /**
   * Initializes the application with the given options
   * @param startOptions - Configuration options for starting the app
   */
  init(startOptions: AppStartOptions) {
    if (isNullish(startOptions.token) || isEmptyString(startOptions.token)) {
      throw new Error(formatErrorMessage(ErrorMessages.INVALID_TOKEN, startOptions.token));
    }
    if (this.startOptions.token !== startOptions.token) {
      this.reset();
    }
    this.startOptions = Object.assign({}, startOptions);
  }

  // === Public API: User Management ===
  /**
   * Identifies a user with the given ID and attributes
   * @param userId - External user ID
   * @param attributes - Optional user attributes
   * @param opts - Optional identify settings (identity token)
   */
  async identify(
    userId: string,
    attributes?: UserTourTypes.Attributes,
    opts?: UserTourTypes.IdentifyOptions,
  ): Promise<UserTourTypes.AttributesWriteResult> {
    // Ensure the SDK has been initialized before calling identify
    this.ensureInit();

    // Convert to string, null/undefined becomes empty string for validation
    const externalUserId = isNullish(userId) ? '' : String(userId);

    if (isEmptyString(externalUserId)) {
      throw new Error(formatErrorMessage(ErrorMessages.INVALID_USER_ID, userId));
    }

    // Reset if user ID has changed
    if (this.externalUserId !== externalUserId) {
      this.reset();
    }

    // Start monitors (idempotent, safe to call multiple times)
    this.startConditionsMonitor();
    this.startURLMonitor();
    this.startTrackerMonitor();

    // Set externalUserId synchronously, BEFORE any await: callers composing
    // identify() with group()/track() via Promise.all (or back-to-back stub
    // queue dispatch) need ensureIdentified() to see it populated. Attributes
    // are intentionally written below the await — they're consumed by the
    // attributeManager change-detection in updateUser, and writing them
    // optimistically would cause a retry-with-same-attrs after a failed
    // upsert to be skipped.
    this.externalUserId = externalUserId;
    // Only overwrite when a token is supplied (same semantics as group()):
    // a tokenless re-identify of the same user — e.g. an attributes refresh —
    // must not wipe the proof the reconnect handshake depends on. A user
    // switch clears it via reset() above.
    if (opts?.token) {
      this.identityToken = opts.token;
      // A grouped session needs a token whose companyId claim still matches,
      // or the next reconnect handshake is rejected under enforcement. Warn
      // only when the claim is actually absent (signature-blind peek), so
      // hosts refreshing a proper combined token get no noise.
      if (this.externalCompanyId) {
        const claims = decodeTokenClaims(opts.token);
        if (claims && claims.companyId == null) {
          log.warn(
            'identify() received an identity token without a companyId claim while a company is set; with enforcement on, the next reconnect will be rejected',
          );
        }
      }
    }

    // Hand the auth credentials to the socket layer; the connection lifecycle
    // is owned there. setAuth is synchronous: it triggers a background
    // connect when needed, and any failure surfaces through the EMIT_TIMEOUT
    // applied to the upsert below.
    this.socketService.setAuth(externalUserId, this.startOptions.token, this.identityToken);
    // setAuth keeps only the identity; the rest of the handshake — company,
    // sessions, client conditions, timers — is synced from here, so a new
    // user starts clean (reset() ran above) and the same user keeps its own.
    this.syncSocketCredentials();

    const userAttributes = this.normalizeAttributes(attributes);
    const result = await this.socketService.upsertUser(
      {
        externalUserId,
        attributes: userAttributes,
      },
      { batch: true },
    );
    if (!result.ok) {
      // Unconfirmed keys leave the cache so the next call carrying them is
      // sent, and a resend of this write cannot land over a newer value.
      this.attributeManager.forgetUserAttributes(Object.keys(userAttributes ?? {}));
      throw new Error(ErrorMessages.FAILED_TO_IDENTIFY_USER);
    }

    // Only update attributes after successful API call so updateUser's
    // change-detection guard remains accurate after retries.
    if (userAttributes) {
      this.attributeManager.setUserAttributes(userAttributes, refusedCodeNames(result.rejected));
    }
    this.trigger(SDKClientEvents.USER_IDENTIFIED_SUCCEEDED, {
      userId: externalUserId,
      attributes: userAttributes,
    });
    return this.reportRejected(result.rejected);
  }

  /**
   * Creates and identifies an anonymous user
   * @param attributes - Optional user attributes
   */
  async identifyAnonymous(
    attributes?: UserTourTypes.Attributes,
  ): Promise<UserTourTypes.AttributesWriteResult> {
    // Ensure the SDK has been initialized before calling identifyAnonymous
    this.ensureInit();

    const key = StorageKeys.IDENTIFY_ANONYMOUS;
    const storageData = storage.getLocalStorage(key) as { userId: string } | undefined;
    let userId = '';
    if (storageData?.userId) {
      userId = storageData.userId;
      // Anonymous ids minted before the `anon-` prefix existed are bare
      // UUIDs. Upgrade them deterministically (same UUID, prefixed) so the
      // server can keep its anonymous exemption strict — relaxing it to bare
      // UUIDs would exempt real users whose ids happen to be UUIDs.
      if (!userId.startsWith('anon-')) {
        userId = `anon-${userId}`;
        storage.setLocalStorage(key, { userId });
      }
    } else {
      userId = `anon-${uuidV4()}`;
      storage.setLocalStorage(key, { userId });
    }
    return await this.identify(userId, attributes);
  }

  /**
   * Updates user attributes
   * @param attributes - New user attributes to update
   * @param opts - Optional identify settings (identity token refresh)
   */
  async updateUser(
    attributes: UserTourTypes.Attributes,
    opts?: UserTourTypes.IdentifyOptions,
  ): Promise<UserTourTypes.AttributesWriteResult> {
    // Ensure the SDK has been initialized before calling updateUser
    const externalUserId = this.ensureIdentify();

    // Store a fresh token BEFORE the change-detection early return, so a
    // token-refresh call with unchanged attributes still reaches the
    // reconnect auth (same rule as updateGroup).
    if (opts?.token) {
      this.applyIdentityToken(opts.token);
    }

    // Check if attributes have actually changed to avoid unnecessary API calls.
    // A key carrying an operation object always counts as changed.
    const userAttributes = this.normalizeAttributes(attributes) ?? {};
    if (!this.attributeManager.userAttrsChanged(userAttributes)) {
      return { rejected: [] }; // No changes detected, skip the update
    }
    // First call API with new attributes
    const result = await this.socketService.upsertUser(
      {
        externalUserId,
        attributes: userAttributes,
      },
      { batch: true },
    );
    if (!result.ok) {
      this.attributeManager.forgetUserAttributes(Object.keys(userAttributes));
      throw new Error(ErrorMessages.FAILED_TO_UPDATE_USER);
    }

    // Only update local state after successful API call
    this.attributeManager.setUserAttributes(userAttributes, refusedCodeNames(result.rejected));
    return this.reportRejected(result.rejected);
  }

  /**
   * Associates user with a company group
   * @param companyId - External company ID
   * @param attributes - Optional company attributes
   * @param opts - Optional group settings
   */
  async group(
    companyId: string,
    attributes?: UserTourTypes.Attributes,
    opts?: UserTourTypes.GroupOptions,
  ): Promise<UserTourTypes.AttributesWriteResult> {
    // Ensure the SDK has been initialized before calling group
    const externalUserId = this.ensureIdentify();

    // Convert to string, null/undefined becomes empty string for validation
    const externalCompanyId = isNullish(companyId) ? '' : String(companyId);

    // Validate company ID
    if (isEmptyString(externalCompanyId)) {
      throw new Error(formatErrorMessage(ErrorMessages.INVALID_COMPANY_ID, companyId));
    }

    // Clear company and membership attributes if company ID has changed
    if (this.externalCompanyId !== externalCompanyId) {
      this.attributeManager.clearCompanyAndMembershipAttributes();
    }

    // Set externalCompanyId synchronously, BEFORE the await, so concurrent
    // ensureGroup() checks (e.g. updateGroup() in a Promise.all) pass without
    // racing this method's ack. Attributes stay below the await for the same
    // reason as identify(): preserves change-detection retry semantics in
    // updateGroup.
    const previousCompanyId = this.externalCompanyId;
    const previousIdentityToken = this.identityToken;
    this.externalCompanyId = externalCompanyId;
    // A token passed here supersedes the identify() one — it must carry a
    // companyId claim matching this group() call.
    if (opts?.token) {
      this.applyIdentityToken(opts.token);
    }

    const companyAttributes = this.normalizeAttributes(attributes);
    const membershipAttributes = this.normalizeAttributes(opts?.membership);
    const result = await this.socketService.upsertCompany(
      {
        externalUserId,
        externalCompanyId,
        attributes: companyAttributes,
        membership: membershipAttributes,
        token: this.identityToken,
      },
      { batch: true },
    );
    if (!result.ok) {
      // Roll back the optimistic identity state: a server-rejected membership
      // claim left in place would ride every reconnect handshake (rejected
      // companyId + non-matching token) and kill the whole connection.
      // Compare-and-restore so a concurrent call's successful update is
      // never clobbered by this rollback.
      if (this.externalCompanyId === externalCompanyId) {
        this.externalCompanyId = previousCompanyId;
      }
      if (opts?.token) {
        this.rollbackIdentityToken(opts.token, previousIdentityToken);
      }
      // Push the restored state to the socket credentials unconditionally:
      // a concurrent message may have synced the optimistic companyId into
      // the reconnect auth while this upsert was in flight, and the tokenless
      // failure path has no other sync. Idempotent and cheap.
      this.syncSocketCredentials();
      this.forgetCompanyWrite(externalCompanyId, companyAttributes, membershipAttributes);
      throw new Error(ErrorMessages.FAILED_TO_UPDATE_COMPANY);
    }

    // Cached only while the core is still on this company: a later group()
    // may have moved on, and this answer would land in that company's cache.
    if (this.externalCompanyId === externalCompanyId) {
      const refused = refusedCodeNames(result.rejected);
      this.attributeManager.setCompanyAttributes(companyAttributes ?? {}, refused);
      this.attributeManager.setMembershipAttributes(membershipAttributes ?? {}, refused);
    }
    // The reconnect credentials must carry the new company now, not after
    // the next server message (which a company with nothing to show never
    // sends): the handshake evaluates on them, and a failed write for this
    // company is replayed only while they name it.
    this.syncSocketCredentials();
    return this.reportRejected(result.rejected);
  }

  /**
   * Updates current company group information
   * @param attributes - Optional company attributes to update
   * @param opts - Optional group settings
   */
  async updateGroup(
    attributes?: UserTourTypes.Attributes,
    opts?: UserTourTypes.GroupOptions,
  ): Promise<UserTourTypes.AttributesWriteResult> {
    const externalUserId = this.ensureIdentify();
    const externalCompanyId = this.ensureGroup();

    // Store a fresh token BEFORE the change-detection early return: hosts
    // refreshing short-lived tokens call updateGroup with unchanged
    // attributes, and the new token must still reach the reconnect auth.
    const previousIdentityToken = this.identityToken;
    if (opts?.token) {
      this.applyIdentityToken(opts.token);
    }

    // Check if attributes have actually changed to avoid unnecessary API calls.
    // A key carrying an operation object always counts as changed.
    const companyAttributes = this.normalizeAttributes(attributes);
    const membershipAttributes = this.normalizeAttributes(opts?.membership);
    const hasCompanyChanged =
      companyAttributes && this.attributeManager.companyAttrsChanged(companyAttributes);
    const hasMembershipChanged =
      membershipAttributes && this.attributeManager.membershipAttrsChanged(membershipAttributes);

    if (!hasCompanyChanged && !hasMembershipChanged) {
      return { rejected: [] }; // No changes detected, skip the update
    }

    // First call API with new attributes
    const result = await this.socketService.upsertCompany(
      {
        externalUserId,
        externalCompanyId,
        attributes: companyAttributes,
        membership: membershipAttributes,
        token: this.identityToken,
      },
      { batch: true },
    );
    if (!result.ok) {
      // Same rollback rule as group(): a rejected claim must not stay on the
      // reconnect auth (compare-and-restore, concurrency-safe).
      if (opts?.token) {
        this.rollbackIdentityToken(opts.token, previousIdentityToken);
      }
      this.forgetCompanyWrite(externalCompanyId, companyAttributes, membershipAttributes);
      throw new Error(ErrorMessages.FAILED_TO_UPDATE_COMPANY);
    }

    // Only update local state after successful API call, and only while the
    // core is still on this company (same rule as group()).
    if (this.externalCompanyId === externalCompanyId) {
      const refused = refusedCodeNames(result.rejected);
      this.attributeManager.setCompanyAttributes(companyAttributes ?? {}, refused);
      this.attributeManager.setMembershipAttributes(membershipAttributes ?? {}, refused);
    }
    return this.reportRejected(result.rejected);
  }

  /**
   * A company write the server did not confirm: its keys leave the caches
   * (see forgetUserAttributes) — unless the core has moved to another
   * company, whose caches this write never touched.
   */
  private forgetCompanyWrite(
    externalCompanyId: string,
    companyAttributes: UserTourTypes.Attributes | undefined,
    membershipAttributes: UserTourTypes.Attributes | undefined,
  ): void {
    if (this.externalCompanyId !== externalCompanyId) {
      return;
    }
    this.attributeManager.forgetCompanyAttributes(Object.keys(companyAttributes ?? {}));
    this.attributeManager.forgetMembershipAttributes(Object.keys(membershipAttributes ?? {}));
  }

  /**
   * Keys the server refused on an upsert (ADR 0020 §6): every other key was
   * written, so the call resolves — but a host that never looks at the
   * return value should still see why a value is missing.
   */
  private reportRejected(
    rejected: UserTourTypes.AttributesWriteResult['rejected'],
  ): UserTourTypes.AttributesWriteResult {
    for (const { codeName, reason } of rejected) {
      log.warn(`Attribute "${codeName}" was not written: ${reason}`);
    }
    return { rejected };
  }

  /**
   * Rewrite deprecated operation spellings (`subtract`, `append`, `prepend`)
   * into the current vocabulary before sending, warning for each rewritten
   * key on every call (ADR 0017 §6). The server accepts only the current
   * vocabulary.
   */
  private normalizeAttributes(
    attributes?: UserTourTypes.Attributes,
  ): UserTourTypes.Attributes | undefined {
    if (!attributes) {
      return attributes;
    }
    return normalizeLegacyAttributeWrites(attributes, ({ codeName, from, to }) => {
      log.warn(
        `Attribute "${codeName}": the ${from} operation is deprecated; it was sent as ${to}`,
      );
    }) as UserTourTypes.Attributes;
  }

  // === Public API: Event Tracking ===
  /**
   * Tracks a custom event
   * @param name - Event name (maps to Event.codeName)
   * @param attributes - Optional event attributes
   * @param opts - Optional track options (e.g. userOnly)
   */
  async track(
    name: string,
    attributes?: UserTourTypes.EventAttributes,
    opts?: UserTourTypes.TrackOptions,
  ): Promise<void> {
    this.ensureIdentify();

    if (isNullish(name) || isEmptyString(name)) {
      throw new Error(formatErrorMessage(ErrorMessages.INVALID_EVENT_NAME, name));
    }

    const result = await this.socketService.trackEvent({
      name,
      attributes: attributes as Record<string, any>,
      userOnly: opts?.userOnly ?? false,
    });

    if (!result) {
      throw new Error(ErrorMessages.FAILED_TO_TRACK_EVENT);
    }
  }

  // === Public API: Content Management ===
  /**
   * Starts a content
   * @param contentId - The content ID to start
   * @param startReason - The reason for starting the content
   * @param opts - The options for starting the content
   * @param batch - Whether to batch the request
   * @returns A promise that resolves when the content is started
   */
  async startContent(
    contentId: string,
    startReason: contentStartReason,
    opts?: UserTourTypes.StartOptions,
    batch = false,
  ) {
    // Ensure the SDK has been initialized before calling startContent
    this.ensureIdentify();
    // Validate content ID
    if (isNullish(contentId) || isEmptyString(contentId)) {
      throw new Error(formatErrorMessage(ErrorMessages.INVALID_CONTENT_ID, contentId));
    }

    // Build start options
    const startOptions = {
      stepCvid: opts?.cvid,
      once: opts?.once,
      continue: opts?.continue,
      startReason,
      contentId,
    };
    // Start the content
    await this.socketService.startContent(startOptions, { batch });
  }

  /**
   * Checks URL for 'usertour' parameter and starts the content if found
   * Removes the parameter from URL after processing
   */
  async checkUrlAndStartContent(): Promise<void> {
    if (!window?.location) {
      return;
    }

    const url = new URL(window.location.href);
    const contentId = url.searchParams.get('usertour');

    if (!contentId) {
      return;
    }

    // Start the content with URL start reason
    await this.startContent(contentId, contentStartReason.START_FROM_URL);

    // Remove the parameter from URL and update browser history
    url.searchParams.delete('usertour');
    window.history.replaceState({}, '', url.toString());
  }

  // === Public API: Configuration ===
  /**
   * Open or close the SDK's console logging (ADR 0019 §3). Effective at once
   * and persisted for future loads until switched off.
   */
  setDebug(enabled: boolean) {
    if (enabled) {
      logger.enabled();
    } else {
      logger.disable();
    }
  }

  /**
   * Sets the base z-index for UI elements
   * @param baseZIndex - The base z-index value to set
   */
  setBaseZIndex(baseZIndex: number) {
    this.baseZIndex = baseZIndex;
  }

  /**
   * Gets the current base z-index value
   * @returns The current base z-index
   */
  getBaseZIndex(): number {
    return this.baseZIndex;
  }

  /**
   * Sets the time allowed for target element to be missing
   * @param seconds - Time in seconds
   * @throws {Error} If seconds is greater than 10
   */
  setTargetMissingSeconds(seconds: number) {
    if (seconds > 10) {
      throw new Error(ErrorMessages.TARGET_MISSING_TIME_EXCEEDED);
    }
    this.targetMissingSeconds = seconds;
  }

  /**
   * Gets the time allowed for target element to be missing
   * @returns Time in seconds
   */
  getTargetMissingSeconds() {
    return this.targetMissingSeconds;
  }

  /**
   * Sets a custom navigation function to override default window.location.href behavior
   * @param customNavigate - Function taking a single string url parameter, or null to use default behavior
   */
  setCustomNavigate(customNavigate: ((url: string) => void) | null) {
    this.customNavigate = customNavigate;
  }

  /**
   * Gets the current custom navigation function
   * @returns The current custom navigation function or null if using default behavior
   */
  getCustomNavigate(): ((url: string) => void) | null {
    return this.customNavigate;
  }

  /**
   * Sets a custom function to handle scrolling elements into view
   * @param customScrollIntoView - Function taking an Element, or null to use default behavior
   */
  setCustomScrollIntoView(customScrollIntoView: ((el: Element) => void) | null) {
    this.customScrollIntoView = customScrollIntoView;
  }

  /**
   * Gets the custom scroll into view function if set
   * @returns The custom scroll into view function or null if using default behavior
   */
  getCustomScrollIntoView(): ((el: Element) => void) | null {
    return this.customScrollIntoView;
  }

  /**
   * Disables JavaScript evaluation via eval
   * When disabled, JAVASCRIPT_EVALUATE actions will be skipped
   */
  disableEvalJs(): void {
    this.evalJsDisabled = true;
  }

  // ── Resource Center public API ───────────────────────────────────────

  openResourceCenter(): void {
    this.activatedResourceCenter?.expand(true);
  }

  closeResourceCenter(): void {
    this.activatedResourceCenter?.expand(false);
  }

  toggleResourceCenter(): void {
    const store = this.activatedResourceCenter?.getSnapshot();
    if (store) {
      this.activatedResourceCenter?.expand(!store.expanded);
    }
  }

  showResourceCenterLauncher(): void {
    this.resourceCenterLauncherHidden = false;
    this.activatedResourceCenter?.setLauncherHidden(false);
  }

  hideResourceCenterLauncher(): void {
    this.resourceCenterLauncherHidden = true;
    this.activatedResourceCenter?.setLauncherHidden(true);
  }

  isResourceCenterOpen(): boolean {
    return this.activatedResourceCenter?.getSnapshot()?.expanded ?? false;
  }

  /**
   * Apply global resource center settings (e.g. launcherHidden) to the
   * currently active instance. Called whenever a new RC session activates
   * or the existing one updates, so settings configured before the RC
   * was active still take effect.
   */
  private applyResourceCenterGlobalSettings(): void {
    this.activatedResourceCenter?.setLauncherHidden(this.resourceCenterLauncherHidden);
  }

  /**
   * Sets a URL filter function to sanitize URLs before they are used for conditions and page tracking
   * @param urlFilter - Function taking a URL string and returning a filtered URL string, or null to use default behavior
   * @example
   * // Remove all query parameters
   * usertour.setUrlFilter(url => {
   *   const parsed = new URL(url);
   *   parsed.search = '';
   *   return parsed.toString();
   * });
   * @example
   * // Remove specific query parameter
   * usertour.setUrlFilter(url => {
   *   const parsed = new URL(url);
   *   parsed.searchParams.delete('token');
   *   return parsed.toString();
   * });
   */
  setUrlFilter(urlFilter: ((url: string) => string) | null): void {
    this.urlFilter = urlFilter;
    // Sync module-level helper so getClientContext() returns filtered URL everywhere
    setUrlFilterFn(urlFilter);
    // Sync URL monitor so checkUrlChange() compares filtered URLs
    this.urlMonitor?.setUrlFilter(urlFilter);
    // Immediately push updated clientContext to server (don't wait for next URL change)
    if (this.socketService.isConnected()) {
      const clientContext = getClientContext();
      this.socketService.updateClientContext(clientContext).catch((err) => {
        log.error('Failed to send the client context after setUrlFilter()', err);
      });
    }
  }

  /**
   * Gets the current URL filter function
   * @returns The current URL filter function or null if not set
   */
  getUrlFilter(): ((url: string) => string) | null {
    return this.urlFilter;
  }

  /**
   * Sets a link URL decorator function to modify link URLs before rendering
   * This is applied to all clickable URLs in the content (images, buttons, richtext links)
   * Useful for adding authentication tokens or other query parameters to external links
   * @param linkUrlDecorator - Function to decorate link URLs, or null to disable decoration
   * @example
   * usertour.setLinkUrlDecorator(url => {
   *   return url + '?token=abc123';
   * });
   */
  setLinkUrlDecorator(linkUrlDecorator: ((url: string) => string) | null): void {
    this.linkUrlDecorator = linkUrlDecorator;
  }

  /**
   * Gets the current link URL decorator function
   * @returns The current link URL decorator or null if not set
   */
  getLinkUrlDecorator(): ((url: string) => string) | null {
    return this.linkUrlDecorator;
  }

  /**
   * Registers a custom input element with optional getValue function
   * @param cssSelector - CSS selector to match custom input elements
   * @param getValue - Optional function to extract value from element
   */
  registerCustomInput(cssSelector: string, getValue?: (el: Element) => string): void {
    customInputRegistry.register(cssSelector, getValue);
  }

  /**
   * Checks if JavaScript evaluation is disabled
   * @returns True if eval is disabled, false otherwise
   */
  isEvalJsDisabled(): boolean {
    return this.evalJsDisabled;
  }

  // === Public API: State Queries ===
  /**
   * Checks if app is in preview mode
   * @returns True if in preview mode, false otherwise
   */
  isPreview() {
    return this.startOptions.mode === SDKSettingsMode.PREVIEW;
  }

  /**
   * Checks if a user is identified
   * @returns True if user info exists, false otherwise
   */
  isIdentified() {
    return Boolean(this.externalUserId);
  }

  /**
   * Ensures that the SDK has been initialized
   * @throws {Error} If init() has not been called
   */
  ensureInit() {
    if (!this.startOptions.token) {
      throw new Error(ErrorMessages.MUST_INIT_FIRST);
    }
  }

  /**
   * Ensures that a user has been identified
   * @returns The external user ID
   * @throws {Error} If init() or identify() has not been called
   */
  ensureIdentify() {
    this.ensureInit();
    if (!this.externalUserId) {
      throw new Error(ErrorMessages.MUST_IDENTIFY_FIRST);
    }
    return this.externalUserId;
  }

  /**
   * Ensures that a group has been set
   * @returns The external company ID
   * @throws {Error} If init(), identify(), or group() has not been called
   */
  ensureGroup() {
    this.ensureIdentify();
    if (!this.externalCompanyId) {
      throw new Error(ErrorMessages.MUST_GROUP_FIRST);
    }
    return this.externalCompanyId;
  }

  /**
   * Ensures that the UI manager has been initialized
   * @throws {Error} If UI manager initialization fails
   */
  async ensureUIManagerInitialized(): Promise<void> {
    const initialized = await this.uiManager.initialize({
      toursStore: this.toursStore,
      checklistsStore: this.checklistsStore,
      launchersStore: this.launchersStore,
      bannersStore: this.bannersStore,
      resourceCentersStore: this.resourceCentersStore,
    });

    if (!initialized) {
      throw new Error(ErrorMessages.UI_INITIALIZATION_MAX_RETRIES_EXCEEDED);
    }
  }

  /**
   * Checks if a content is currently active
   * @param contentId - The content ID to check
   * @returns True if the content is currently active, false otherwise
   */
  isStarted(contentId: string) {
    return (
      this.activatedChecklist?.getContentId() === contentId ||
      this.activatedTour?.getContentId() === contentId ||
      this.activatedBanner?.getContentId() === contentId ||
      this.activatedResourceCenter?.getContentId() === contentId ||
      this.launchers.some((launcher) => launcher.getContentId() === contentId)
    );
  }

  // === Public API: Utilities ===
  /**
   * Gets user attributes from attribute manager
   * @returns Current user attributes
   */
  getUserAttributes(): UserTourTypes.Attributes {
    return this.attributeManager.getUserAttributes();
  }

  /**
   * Gets client conditions from conditions monitor
   * @returns Array of client conditions with their current state
   */
  getClientConditions(): ClientCondition[] {
    if (!this.conditionsMonitor) return [];

    const conditions = this.conditionsMonitor.getConditions();
    const activeIds = this.conditionsMonitor.getActiveConditionIds();

    return conditions.map((trackCondition) => ({
      contentId: trackCondition.contentId,
      versionId: trackCondition.versionId,
      contentType: trackCondition.contentType,
      conditionId: trackCondition.condition.id,
      isActive: activeIds.has(trackCondition.condition.id),
    }));
  }

  /**
   * Gets the shared SocketService instance
   */
  getSocketService(): UsertourSocket {
    return this.socketService;
  }

  // === Public API: Lifecycle ===
  /**
   * Previews content with specified options and test user
   * @param startOptions - Preview configuration options including test user ID
   */
  async preview(startOptions: AppStartOptions & { userId: string }) {
    this.startOptions = Object.assign({}, startOptions);
    //reset
    this.reset();
  }

  /**
   * Ends all active content and resets the application
   */
  async endAll(): Promise<void> {
    await this.socketService.endAllContent();
  }

  /**
   * Resets the application state
   */
  reset() {
    // Cleanup user data
    this.cleanupUserData();
    // Cleanup activated tour
    this.cleanupActivatedTour();
    // Cleanup activated checklist
    this.cleanupActivatedChecklist();
    // Cleanup activated banner
    this.cleanupActivatedBanner();
    // Cleanup activated resource center
    this.cleanupActivatedResourceCenter();
    // Cleanup launchers
    this.cleanupLaunchers();
    // Cleanup trackers
    this.activeTrackers.clear();
    // Clear all unacked tasks
    this.clearUnackedTasks();
    // Note: We don't clear dismissedSessionIds here because:
    // 1. SessionId is globally unique (primary key)
    // 2. Dismissed is a terminal state - once dismissed, the session should never be re-displayed
    // 3. Even if user/environment changes, if it's the same sessionId, it should remain dismissed
    // Cleanup condition monitor
    this.cleanupConditionsMonitor();
    // Cleanup wait timer monitor
    this.cleanupWaitTimerMonitor();
    // The session ends here: the socket disconnects and forgets its
    // credentials and failed writes, so a transport blip cannot reconnect
    // as the user who just left and evaluate for them; the next identify()
    // starts a fresh connection (ADR 0018 §1).
    this.socketService.disconnect();
    // Cleanup tracker monitor
    this.cleanupTrackerMonitor();
    // Stop URL monitor
    this.cleanupURLMonitor();
    // Cleanup time manager
    this.cleanupTimeManager();
    // Cleanup rules evaluator manager
    this.cleanupRulesEvaluatorManager();
  }

  // === Event Listeners Initialization ===
  /**
   * Initializes DOM event listeners for the application
   */
  private initializeEventListeners() {
    this.setupUIManagerInitialization();
    this.setupDOMReadyTrigger();
    this.setupPreviewMessageListener();

    // Subscribe to succeeded server message to refresh credentials immediately
    this.on(SDKClientEvents.SERVER_MESSAGE_SUCCEEDED, this.handleServerMessageSucceeded);
    this.once(SDKClientEvents.USER_IDENTIFIED_SUCCEEDED, this.handleUserFirstIdentified);
  }

  /**
   * Sets up UI manager initialization when DOM is loaded
   * Note: Called by event system which doesn't await
   */
  private setupUIManagerInitialization() {
    this.once(SDKClientEvents.DOM_LOADED, () => {
      // A final failure is already reported by the critical line; an
      // unhandled rejection would print a second one and get filed by the
      // host's error tracker.
      this.ensureUIManagerInitialized().catch(() => undefined);
    });
  }

  /**
   * Sets up DOM ready state checking and triggers DOM_LOADED event
   */
  private setupDOMReadyTrigger() {
    if (document?.readyState !== 'loading') {
      this.trigger(SDKClientEvents.DOM_LOADED);
    } else if (document) {
      on(document, 'DOMContentLoaded', () => {
        this.trigger(SDKClientEvents.DOM_LOADED);
      });
    }
  }

  /**
   * Sets up window message listener for preview messages
   */
  private setupPreviewMessageListener() {
    if (window) {
      on(window, 'message', this.handlePreviewMessage);
    }
  }

  /**
   * Initialize socket event listeners
   * This method sets up all WebSocket event handlers after socket is initialized
   */
  private initializeSocketEventListeners(): void {
    this.socketService.onQueue(WebSocketEvents.SERVER_MESSAGE, this.handleServerMessage);
    // Connection recovery (ADR 0018): the replay decisions are made against
    // where the core is now — the company from the moment group() was
    // called — not against credentials the server has yet to confirm.
    this.socketService.setTargetResolver(() => ({
      externalUserId: this.externalUserId,
      externalCompanyId: this.externalCompanyId,
    }));
    // Fold a replayed write into the attribute cache exactly as the original
    // call would have.
    this.socketService.onWriteReplayed((write) => {
      // A replay answered after the core moved on — identify() of another
      // user, group() of another company, reset() — must not land in the
      // cache of where the core is now.
      if (write.params.externalUserId !== this.externalUserId) {
        return;
      }
      // A replay is answered like a direct call: the keys the server refused
      // leave the cache and are reported.
      const refused = refusedCodeNames(write.rejected);
      if (write.kind === 'user') {
        this.attributeManager.setUserAttributes(write.params.attributes ?? {}, refused);
        // The identify() this write came from rejected on its timeout, so
        // its success event never fired; a shared-link start still waits
        // for one.
        this.trigger(SDKClientEvents.USER_IDENTIFIED_SUCCEEDED, {
          userId: write.params.externalUserId,
          attributes: write.params.attributes,
        });
        this.reportRejected(write.rejected);
        return;
      }
      if (write.params.externalCompanyId !== this.externalCompanyId) {
        return;
      }
      this.attributeManager.setCompanyAttributes(write.params.attributes ?? {}, refused);
      this.attributeManager.setMembershipAttributes(write.params.membership ?? {}, refused);
      this.reportRejected(write.rejected);
    });
  }

  // === Message Handling ===
  /**
   * Handles preview messages from the builder
   * @param e - Message event containing preview data
   */
  private async handlePreviewMessage(e: MessageEvent) {
    const message = getValidMessage(e);
    if (!message) {
      return;
    }
    const { environmentId, contentId, idempotentKey } = message;
    const { versionId, testUser, token } = message;
    if (message.kind !== MESSAGE_START_FLOW_WITH_TOKEN) {
      return;
    }
    // send success message to builder
    sendPreviewSuccessMessage(idempotentKey);
    await this.preview({
      token,
      environmentId,
      contentId,
      versionId,
      mode: SDKSettingsMode.PREVIEW,
      userId: testUser.id,
    });
  }

  /**
   * Handle server message by routing to appropriate handler
   * Messages are processed serially at the socket level to maintain order and prevent race conditions
   * @param message - The server message containing kind and payload
   * @returns Promise<boolean> - True if message was handled successfully
   */
  private async handleServerMessage(message: unknown): Promise<boolean> {
    const success = await this.serverMessageHandlerManager.handleServerMessage(
      message,
      this.serverMessageHandlerContext,
    );

    // Trigger appropriate event based on result
    const event = success
      ? SDKClientEvents.SERVER_MESSAGE_SUCCEEDED
      : SDKClientEvents.SERVER_MESSAGE_FAILED;
    this.trigger(event, message);

    return success;
  }

  /**
   * Handles server message succeeded event - refreshes socket credentials
   * @param _payload - Contains kind and payload from the succeeded message (unused)
   * @returns void
   */
  private handleServerMessageSucceeded(_payload: unknown) {
    this.syncSocketCredentials();
  }

  /**
   * Handles user identified event - checks URL for 'usertour' parameter and starts the content if found
   * Note: Called by event system which doesn't await, so we fire-and-forget
   */
  private handleUserFirstIdentified() {
    this.checkUrlAndStartContent();
  }

  // === Session Management ===

  /**
   * Checks if a session has been dismissed by the user
   * Once a session is dismissed, it will never be re-displayed (terminal state)
   * @param sessionId - The session ID to check
   * @returns True if the session has been dismissed
   */
  private isSessionDismissed(sessionId: string | undefined): boolean {
    // Only check non-empty session IDs
    // isEmptyString only returns true for empty strings, not undefined/null
    // So we need to check isNullish first
    if (isNullish(sessionId) || isEmptyString(sessionId)) {
      return false;
    }
    return this.dismissedSessionIds.has(sessionId);
  }

  /**
   * Marks a session as dismissed when component is closed
   * This is a terminal state - dismissed sessions will never be re-displayed
   * Once dismissed, the session will never be re-displayed, regardless of server messages
   * @param sessionId - The session ID to mark as dismissed
   */
  private markSessionAsDismissed(sessionId: string | undefined): void {
    // Only mark non-empty session IDs as dismissed
    // isEmptyString only returns true for empty strings, not undefined/null
    // So we need to check isNullish first
    if (isNullish(sessionId) || isEmptyString(sessionId)) {
      return;
    }
    this.dismissedSessionIds.add(sessionId);
  }

  /**
   * Sets up a dismiss handler for a component
   * Marks the session as dismissed when component is closed (terminal state)
   * @param component - The component instance
   * @param onDismissed - Optional callback when component is dismissed
   */
  private setupComponentDismissHandler(
    component:
      | UsertourTour
      | UsertourChecklist
      | UsertourLauncher
      | UsertourBanner
      | UsertourResourceCenter,
    onDismissed?: (sessionId: string) => void | Promise<void>,
  ): void {
    component.on(SDKClientEvents.COMPONENT_CLOSED, (eventData: unknown) => {
      const { sessionId } = eventData as { sessionId?: string };
      // Only process non-empty session IDs
      // isEmptyString only returns true for empty strings, not undefined/null
      // So we need to check isNullish first
      if (isNullish(sessionId) || isEmptyString(sessionId)) {
        return;
      }
      // Mark this session as dismissed (terminal state)
      // Once dismissed, the session will never be re-displayed
      this.markSessionAsDismissed(sessionId);
      // Call optional callback (fire-and-forget since event system doesn't await)
      if (onDismissed) {
        onDismissed(sessionId);
      }
    });
  }

  /**
   * Sets the flow session and manages tour lifecycle
   * @param session - The SDK content session to set
   */
  private async setFlowSession(session: CustomContentSession): Promise<boolean> {
    // Check if this session has been dismissed (terminal state check)
    if (this.isSessionDismissed(session.id)) {
      log.debug(`Ignoring SetFlowSession for a dismissed session (${session.id})`);
      return false;
    }

    const hasActivatedTour = this.activatedTour !== null;

    if (this.activatedTour) {
      if (this.activatedTour.getSessionId() === session.id) {
        this.activatedTour.updateSession(session);
        await this.activatedTour.refreshStoreData();
        return true;
      }
      this.cleanupActivatedTour();
    }

    // Create new tour
    const usertourTour = new UsertourTour(this, new UsertourSession(session));
    this.setupComponentDismissHandler(usertourTour, async (sessionId) => {
      if (this.activatedTour?.getSessionId() === sessionId) {
        this.cleanupActivatedTour();
        await this.expandChecklist();
      }
    });
    this.activatedTour = usertourTour;
    // Sync store
    this.syncToursStore([this.activatedTour]);
    // Show tour from the session current step
    if (session.currentStep?.cvid) {
      await usertourTour.showStepByCvid(session.currentStep?.cvid, false);
    } else {
      await usertourTour.showStepByIndex(0, false);
    }
    if (!hasActivatedTour) {
      await this.collapseChecklist();
    }
    return true;
  }

  /**
   * Unsets the flow session and destroys the tour
   * @param sessionId - The session ID to unset
   */
  private async unsetFlowSession(sessionId: string): Promise<boolean> {
    if (!this.activatedTour || this.activatedTour.getSessionId() !== sessionId) {
      return false;
    }
    // Note: We don't clear dismissedSessionIds here because dismissed is a terminal state
    // Once a user dismisses a session, it should never be re-displayed, regardless of server messages
    this.cleanupActivatedTour();
    await this.expandChecklist();
    return true;
  }

  /**
   * Forces a step to be shown in the tour
   * @param sessionId - The session ID to force go to step
   * @param stepId - The step ID to force go to step
   * @returns True if the step was forced to be shown, false otherwise
   */
  private async forceGoToStep(sessionId: string, stepId: string): Promise<boolean> {
    if (!this.activatedTour || this.activatedTour.getSessionId() !== sessionId) {
      return false;
    }
    await this.activatedTour.showStepById(stepId, false);
    return true;
  }

  /**
   * Sets the checklist session and manages checklist lifecycle
   * @param session - The SDK content session to set
   */
  private async setChecklistSession(session: CustomContentSession): Promise<boolean> {
    // Check if this session has been dismissed (terminal state check)
    if (this.isSessionDismissed(session.id)) {
      log.debug(`Ignoring SetChecklistSession for a dismissed session (${session.id})`);
      return false;
    }

    if (this.activatedChecklist) {
      if (this.activatedChecklist.getSessionId() === session.id) {
        await this.activatedChecklist.update(session);
        await this.expandChecklist();
        return true;
      }
      this.cleanupActivatedChecklist();
    }

    // Create new checklist
    this.activatedChecklist = new UsertourChecklist(this, new UsertourSession(session));
    this.setupComponentDismissHandler(this.activatedChecklist, (sessionId) => {
      if (this.activatedChecklist?.getSessionId() === sessionId) {
        this.cleanupActivatedChecklist();
      }
    });
    // Sync store
    this.syncChecklistsStore([this.activatedChecklist]);
    // Show checklist with appropriate expanded state
    await this.activatedChecklist.show();
    return true;
  }

  /**
   * Sets the banner session and manages banner lifecycle
   * @param session - The SDK content session to set
   */
  private async setBannerSession(session: CustomContentSession): Promise<boolean> {
    if (this.isSessionDismissed(session.id)) {
      log.debug(`Ignoring SetBannerSession for a dismissed session (${session.id})`);
      return false;
    }

    if (this.activatedBanner) {
      if (this.activatedBanner.getSessionId() === session.id) {
        await this.activatedBanner.update(session);
        return true;
      }
      this.cleanupActivatedBanner();
    }

    this.activatedBanner = new UsertourBanner(this, new UsertourSession(session));
    this.setupComponentDismissHandler(this.activatedBanner, (sessionId) => {
      if (this.activatedBanner?.getSessionId() === sessionId) {
        this.cleanupActivatedBanner();
      }
    });
    this.syncBannersStore([this.activatedBanner]);
    await this.activatedBanner.show();
    return true;
  }

  /**
   * Unsets the checklist session and destroys the checklist
   * @param sessionId - The session ID to unset
   */
  private async unsetChecklistSession(sessionId: string): Promise<boolean> {
    if (!this.activatedChecklist || this.activatedChecklist.getSessionId() !== sessionId) {
      return false;
    }
    // Note: We don't clear dismissedSessionIds here because dismissed is a terminal state
    // Once a user dismisses a session, it should never be re-displayed, regardless of server messages
    this.cleanupActivatedChecklist();
    // Note: We don't clear unacked tasks here because they might be needed
    // if ChecklistTaskCompleted messages arrive after unsetChecklistSession
    return true;
  }

  /**
   * Unsets the banner session and destroys the banner
   * @param sessionId - The session ID to unset
   */
  private async unsetBannerSession(sessionId: string): Promise<boolean> {
    if (!this.activatedBanner || this.activatedBanner.getSessionId() !== sessionId) {
      return false;
    }
    this.cleanupActivatedBanner();
    return true;
  }

  /**
   * Sets the resource center session and manages resource center lifecycle
   * @param session - The SDK content session to set
   */
  private async setResourceCenterSession(session: CustomContentSession): Promise<boolean> {
    if (this.isSessionDismissed(session.id)) {
      log.debug(`Ignoring SetResourceCenterSession for a dismissed session (${session.id})`);
      return false;
    }

    if (this.activatedResourceCenter) {
      if (this.activatedResourceCenter.getSessionId() === session.id) {
        await this.activatedResourceCenter.update(session);
        this.applyResourceCenterGlobalSettings();
        return true;
      }
      this.cleanupActivatedResourceCenter();
    }

    this.activatedResourceCenter = new UsertourResourceCenter(this, new UsertourSession(session));
    this.setupComponentDismissHandler(this.activatedResourceCenter, (sessionId) => {
      if (this.activatedResourceCenter?.getSessionId() === sessionId) {
        this.cleanupActivatedResourceCenter();
      }
    });
    this.syncResourceCentersStore([this.activatedResourceCenter]);
    await this.activatedResourceCenter.show();
    this.applyResourceCenterGlobalSettings();
    return true;
  }

  /**
   * Unsets the resource center session and destroys the resource center
   * @param sessionId - The session ID to unset
   */
  private async unsetResourceCenterSession(sessionId: string): Promise<boolean> {
    if (
      !this.activatedResourceCenter ||
      this.activatedResourceCenter.getSessionId() !== sessionId
    ) {
      return false;
    }
    this.cleanupActivatedResourceCenter();
    return true;
  }

  /**
   * Expands the checklist
   */
  private async expandChecklist() {
    if (!this.activatedTour && this.activatedChecklist?.isExpandable()) {
      await this.activatedChecklist.expand(true);
    }
  }

  /**
   * Collapses the checklist
   */
  private async collapseChecklist() {
    if (this.activatedChecklist) {
      await this.activatedChecklist.expand(false);
    }
  }

  // === Launcher Management ===
  /**
   * Adds a launcher to the application
   * @param session - The SDK content session to add the launcher to
   * @returns True if the launcher was added, false otherwise
   */
  private async addLauncher(session: CustomContentSession): Promise<boolean> {
    // Check if this session has been dismissed (terminal state check)
    if (this.isSessionDismissed(session.id)) {
      log.debug(`Ignoring AddLauncher for a dismissed session (${session.id})`);
      return false;
    }

    const contentId = session.content.id;
    const existingLauncher = this.launchers.find(
      (launcher) => launcher.getContentId() === contentId,
    );
    if (existingLauncher) {
      existingLauncher.updateSession(session);
      await existingLauncher.refreshStoreData();
      await existingLauncher.show();
      return true;
    }
    const launcher = new UsertourLauncher(this, new UsertourSession(session));
    this.setupComponentDismissHandler(launcher, async (_sessionId) => {
      // Launcher removal is based on contentId, not sessionId
      await this.removeLauncher(contentId);
    });
    this.launchers.push(launcher);
    // Sync store
    this.syncLaunchersStore(this.launchers);
    await launcher.show();
    return true;
  }

  /**
   * Removes a launcher from the application
   * @param contentId - The content ID to remove the launcher from
   * @returns True if the launcher was removed, false otherwise
   */
  private async removeLauncher(contentId: string): Promise<boolean> {
    const launcher = this.launchers.find((launcher) => launcher.getContentId() === contentId);
    if (!launcher) {
      return false;
    }
    // Note: We don't clear dismissedSessionIds here because dismissed is a terminal state
    // Once a user dismisses a session, it should never be re-displayed, regardless of server messages
    // Destroy the launcher
    launcher.destroy();
    // Remove from the launchers array
    this.launchers = this.launchers.filter((item) => item.getContentId() !== contentId);
    this.syncLaunchersStore(this.launchers);
    return true;
  }

  // === Tracker Management ===
  /**
   * Register or update a tracker session.
   * SDK evaluates tracker conditions in the monitor loop and only reports on false→true edges.
   */
  private async addTracker(session: CustomContentSession): Promise<boolean> {
    const contentId = session.content.id;
    const existing = this.activeTrackers.get(contentId);
    this.activeTrackers.set(contentId, {
      session,
      // Preserve activation state when same contentId is replaced by a newer version.
      isActive: existing?.isActive ?? false,
    });
    // Evaluate promptly so always-active/no-condition trackers can be reported without waiting.
    await this.evaluateTrackers();

    return true;
  }

  /**
   * Remove a tracker from SDK evaluation state.
   */
  private async removeTracker(contentId: string): Promise<boolean> {
    return this.activeTrackers.delete(contentId);
  }

  // === Store Synchronization ===
  /**
   * Synchronizes tours store
   */
  private syncToursStore(tours: UsertourTour[]) {
    this.toursStore.setData([...tours]);
  }

  /**
   * Synchronizes checklists store
   */
  private syncChecklistsStore(checklists: UsertourChecklist[]) {
    this.checklistsStore.setData([...checklists]);
  }

  /**
   * Synchronizes launchers store
   */
  private syncLaunchersStore(launchers: UsertourLauncher[]) {
    this.launchersStore.setData([...launchers]);
  }

  /**
   * Synchronizes banners store
   */
  private syncBannersStore(banners: UsertourBanner[]) {
    this.bannersStore.setData([...banners]);
  }

  /**
   * Synchronizes resource centers store
   */
  private syncResourceCentersStore(resourceCenters: UsertourResourceCenter[]) {
    this.resourceCentersStore.setData([...resourceCenters]);
  }

  // === Server Message Handler Context Initialization ===
  /**
   * Initializes the server message handler context
   * Must be called after autoBind(this) to ensure methods are bound
   */
  private initializeServerMessageHandlerContext(): void {
    this.serverMessageHandlerContext = {
      setFlowSession: this.setFlowSession,
      forceGoToStep: this.forceGoToStep,
      unsetFlowSession: this.unsetFlowSession,
      setChecklistSession: this.setChecklistSession,
      unsetChecklistSession: this.unsetChecklistSession,
      setBannerSession: this.setBannerSession,
      unsetBannerSession: this.unsetBannerSession,
      setResourceCenterSession: this.setResourceCenterSession,
      unsetResourceCenterSession: this.unsetResourceCenterSession,
      addLauncher: this.addLauncher,
      removeLauncher: this.removeLauncher,
      addTracker: this.addTracker,
      removeTracker: this.removeTracker,
      trackClientCondition: this.trackClientCondition,
      removeConditions: this.removeConditions,
      startConditionWaitTimer: this.startConditionWaitTimer,
      cancelConditionWaitTimer: this.cancelConditionWaitTimer,
      addUnackedTask: this.addUnackedTask,
    };
  }

  // === Monitor Initialization ===
  /**
   * Creates and initializes condition monitor
   */
  private initializeConditionsMonitor() {
    this.conditionsMonitor = new UsertourConditionsMonitor({ autoStart: false });

    // Start monitoring when uiManager initialization completes
    // When uiManager.initialize() succeeds, DOM is definitely ready for condition evaluation
    // This is more reliable than checking document.readyState because uiManager.initialize()
    // requires document.body to exist and be ready for DOM operations
    this.uiManager.once(SDKClientEvents.INITIALIZATION_COMPLETE, () => {
      this.conditionsMonitor?.start();
    });

    // Listen for condition state change events
    this.conditionsMonitor.on(SDKClientEvents.CONDITION_STATE_CHANGED, (eventData: unknown) => {
      const changeEvent = eventData as ConditionStateChangeEvent;
      const { trackCondition, state } = changeEvent;

      if (!trackCondition?.condition?.id) {
        return;
      }

      // The reconnect handshake must carry this state before the report
      // below can be lost to a disconnect: the server takes the handshake's
      // conditions as they are and does not track them again, and the
      // monitor reports changes only — a stale state would stand until the
      // element toggled once more (ADR 0018 §6, as for a fired timer).
      this.syncSocketCredentials();
      // Toggle client condition
      this.socketService.toggleClientCondition(
        {
          contentId: trackCondition.contentId,
          versionId: trackCondition.versionId,
          conditionId: trackCondition.condition.id,
          contentType: trackCondition.contentType,
          isActive: state === 'activated',
        },
        { batch: true },
      );
    });
  }

  /**
   * Creates and initializes wait timer monitor
   */
  private initializeWaitTimerMonitor() {
    this.waitTimerMonitor = new ConditionWaitTimersMonitor({ autoStart: true });

    // Listen for wait timer state change events
    this.waitTimerMonitor.on(
      SDKClientEvents.WAIT_TIMER_STATE_CHANGED,
      async (eventData: unknown) => {
        const changeEvent = eventData as WaitTimerStateChangeEvent;
        if (!changeEvent?.condition?.versionId || changeEvent.state !== 'fired') {
          return;
        }

        // The reconnect handshake must declare this timer as fired before
        // the report below can be lost to a disconnect: the server neither
        // re-issues a timer it holds nor starts content on one it thinks is
        // still running (ADR 0018 §6).
        this.syncSocketCredentials();
        // Handle timer firing - could trigger next step or other actions
        const result = await this.socketService.fireConditionWaitTimer(
          {
            versionId: changeEvent.condition.versionId,
          },
          { batch: true },
        );
        if (!result) {
          log.error(
            `Failed to report the fired wait timer (version ${changeEvent.condition.versionId})`,
          );
        }
      },
    );
  }

  /**
   * Creates and initializes URL monitor
   */
  private initializeURLMonitor() {
    this.urlMonitor = new UsertourURLMonitor({
      autoStart: true,
      interval: 500,
      urlFilter: this.urlFilter ?? undefined,
    });

    // Listen for URL change events
    this.urlMonitor.on(SDKClientEvents.URL_CHANGED, async () => {
      const clientContext = getClientContext();
      await this.socketService.updateClientContext(clientContext, { batch: true });
      await this.checkUrlAndStartContent();
    });
  }

  // === Monitor Management ===
  /**
   * Starts the condition monitor
   */
  private startConditionsMonitor() {
    this.conditionsMonitor?.start();
  }

  /**
   * Tracks a client condition
   * @param condition - The condition to track
   */
  private async trackClientCondition(condition: TrackCondition): Promise<boolean> {
    this.conditionsMonitor?.addConditions([condition]);
    return true;
  }

  /**
   * Removes conditions from the condition monitor
   * @param conditionIds - The IDs of the conditions to remove
   */
  private async removeConditions(conditionIds: string[]): Promise<boolean> {
    this.conditionsMonitor?.removeConditions(conditionIds);
    return true;
  }

  /**
   * Starts a wait timer condition
   * @param condition - The condition to start
   */
  private async startConditionWaitTimer(condition: ConditionWaitTimer): Promise<boolean> {
    this.waitTimerMonitor?.addWaitTimer(condition);
    return true;
  }

  /**
   * Cancels a wait timer condition
   * @param condition - The condition to cancel
   */
  private async cancelConditionWaitTimer(condition: ConditionWaitTimer): Promise<boolean> {
    this.waitTimerMonitor?.cancelWaitTimer(condition.versionId);
    return true;
  }

  /**
   * Starts the URL monitor
   */
  private startURLMonitor() {
    this.urlMonitor?.start();
  }

  /**
   * Starts tracker condition monitor loop.
   * Tracker condition activation is evaluated entirely on the SDK side.
   */
  private startTrackerMonitor() {
    if (this.trackerMonitorIntervalId) {
      return;
    }
    this.trackerMonitorIntervalId = `${this.id}-tracker-monitor`;
    timerManager.setInterval(
      this.trackerMonitorIntervalId,
      async () => {
        await this.evaluateTrackers();
      },
      1000,
    );
  }

  private async evaluateTrackers(): Promise<void> {
    if (this.isTrackerMonitorRunning || this.activeTrackers.size === 0) {
      return;
    }
    this.isTrackerMonitorRunning = true;
    try {
      for (const [contentId, trackerState] of this.activeTrackers.entries()) {
        const { session } = trackerState;
        const rules = session.version.config?.autoStartRules ?? [];
        const attrs = session.attributes ?? [];
        let isActive = false;

        if (rules.length === 0) {
          // Empty rules means always active.
          isActive = true;
        } else {
          const evaluator = rulesEvaluatorManager.getEvaluator(contentId);
          const evaluatedRules = await evaluator.evaluate(rules, attrs);
          isActive = isConditionsActived(evaluatedRules);
        }

        let reported = false;
        if (!trackerState.isActive && isActive) {
          reported = await this.socketService.trackTrackerEvent({
            contentId: session.content.id,
            versionId: session.version.id,
          });
        }

        // The report ack above is a real yield point: an ADD_TRACKER may have
        // replaced this entry (or a REMOVE_TRACKER deleted it) while it was in
        // flight. Writing the captured session back would clobber the newer
        // distribution — the server got its ack and will never re-send.
        const latestState = this.activeTrackers.get(contentId);
        if (!latestState) {
          continue;
        }
        if (latestState.session === session) {
          this.activeTrackers.set(contentId, { session, isActive });
        } else if (reported) {
          // Replaced mid-flight but the occurrence was recorded — carry the
          // edge onto the new session so it does not double-report (the dedup
          // window keys on versionId and would not catch a cross-version pair).
          this.activeTrackers.set(contentId, { session: latestState.session, isActive: true });
        }
        // Replaced and nothing recorded (report rejected or no edge fired):
        // leave the new entry as addTracker stored it, so the next tick
        // evaluates the new definition fresh and can still report the edge.
      }
    } finally {
      this.isTrackerMonitorRunning = false;
    }
  }

  // === Socket Credentials Synchronization ===
  /**
   * Single write path for identity-token updates once a session exists:
   * stores the token and pushes it into the socket's reconnect credentials
   * immediately. The socket layer restarts a dead connection when the token
   * changes, so a refreshed token can recover from a rejected handshake.
   */
  private applyIdentityToken(token: string): void {
    this.identityToken = token;
    this.syncSocketCredentials();
  }

  /**
   * Compare-and-restore rollback for a failed optimistic token update: only
   * undo when the current token is still the one this call installed, so a
   * concurrent call's successful update is never clobbered.
   */
  private rollbackIdentityToken(installedToken: string, previousToken: string | undefined): void {
    if (this.identityToken === installedToken) {
      this.identityToken = previousToken;
      this.syncSocketCredentials();
    }
  }

  /**
   * Synchronizes socket credentials
   */
  private syncSocketCredentials() {
    const clientConditions = this.getClientConditions();
    const clientContext = getClientContext();
    const externalCompanyId = this.externalCompanyId;
    const externalUserId = this.externalUserId;
    const token = this.startOptions.token;
    const flowSessionId = this.activatedTour?.getSessionId();
    const checklistSessionId = this.activatedChecklist?.getSessionId();
    const bannerSessionId = this.activatedBanner?.getSessionId();
    const resourceCenterSessionId = this.activatedResourceCenter?.getSessionId();
    const launchers = this.launchers.map((l) => l.getContentId());
    // Running and fired wait timers travel with the handshake so a reconnect
    // neither restarts nor forgets them (ADR 0018 §6).
    const waitTimers = this.waitTimerMonitor?.getWaitTimers() ?? [];
    this.socketService.updateCredentials({
      clientConditions,
      clientContext,
      externalCompanyId,
      externalUserId,
      identityToken: this.identityToken,
      token,
      flowSessionId,
      checklistSessionId,
      bannerSessionId,
      resourceCenterSessionId,
      launchers,
      waitTimers,
    });
  }

  // === Cleanup ===
  /**
   * Cleans up user-related data including external IDs and attributes
   */
  private cleanupUserData() {
    this.externalUserId = undefined;
    this.externalCompanyId = undefined;
    this.identityToken = undefined;
    // Cleanup all attributes using the attribute manager
    this.attributeManager.cleanup();
  }

  /**
   * Cleans up all tours from the application
   */
  private cleanupActivatedTour() {
    // Destroy all tours
    this.activatedTour?.destroy();
    this.activatedTour = null;
    this.toursStore.setData(undefined);
  }

  /**
   * Cleans up the activated checklist
   */
  private cleanupActivatedChecklist() {
    // Destroy the checklist
    this.activatedChecklist?.destroy();
    this.activatedChecklist = null;
    this.checklistsStore.setData(undefined);
    // Note: We don't clear unacked tasks here because they might be needed
    // if ChecklistTaskCompleted messages arrive after unsetChecklistSession.
    // Unacked tasks will be cleared when the checklist is expanded or when
    // a new checklist session is set for the same sessionId.
  }

  /**
   * Cleans up the activated banner
   */
  private cleanupActivatedBanner() {
    this.activatedBanner?.destroy();
    this.activatedBanner = null;
    this.bannersStore.setData(undefined);
  }

  /**
   * Cleans up the activated resource center
   */
  private cleanupActivatedResourceCenter() {
    this.activatedResourceCenter?.destroy();
    this.activatedResourceCenter = null;
    this.resourceCentersStore.setData(undefined);
  }

  // === Unacked Tasks Management ===
  /**
   * Adds a task ID to the unacked tasks set for the activated checklist session
   * @param sessionId - The session ID to add the task to
   * @param taskId - The task ID to add
   * @returns True if the task was added successfully
   */
  async addUnackedTask(sessionId: string, taskId: string): Promise<boolean> {
    if (!this.taskIsUnacked.has(sessionId)) {
      this.taskIsUnacked.set(sessionId, new Set<string>());
    }
    this.taskIsUnacked.get(sessionId)?.add(taskId);
    return true;
  }

  /**
   * Clears unacked tasks
   * @param sessionId - Optional. If provided, clears tasks for the specific session; otherwise clears all tasks
   */
  clearUnackedTasks(sessionId?: string): void {
    if (sessionId !== undefined) {
      this.taskIsUnacked.delete(sessionId);
    } else {
      this.taskIsUnacked.clear();
    }
  }

  /**
   * Gets the unacked tasks for a specific session
   * @param sessionId - The session ID to get unacked tasks for
   * @returns Set of unacked task IDs for the session, or undefined if no unacked tasks exist
   */
  getUnackedTasks(sessionId: string): Set<string> | undefined {
    return this.taskIsUnacked.get(sessionId);
  }

  /**
   * Cleans up all launchers from the application
   */
  private cleanupLaunchers() {
    // Cleanup all launchers
    for (const launcher of this.launchers) {
      launcher.destroy();
    }
    this.launchers = [];
    this.syncLaunchersStore(this.launchers);
  }

  /**
   * Cleans up the condition monitor
   */
  private cleanupConditionsMonitor(): void {
    this.conditionsMonitor?.cleanup();
  }

  /**
   * Cleans up the wait timer monitor
   */
  private cleanupWaitTimerMonitor(): void {
    this.waitTimerMonitor?.cleanup();
  }

  /**
   * Clean up the URL monitor
   */
  private cleanupURLMonitor(): void {
    this.urlMonitor?.cleanup();
  }

  private cleanupTrackerMonitor(): void {
    if (!this.trackerMonitorIntervalId) {
      return;
    }
    timerManager.clearInterval(this.trackerMonitorIntervalId);
    this.trackerMonitorIntervalId = null;
    this.isTrackerMonitorRunning = false;
  }

  /**
   * Clean up the timer manager
   */
  private cleanupTimeManager(): void {
    timerManager.cleanup();
  }

  /**
   * Clean up all rules evaluators
   */
  private cleanupRulesEvaluatorManager(): void {
    rulesEvaluatorManager.cleanup();
  }
}
